import { auth } from "./firebase";
import { ACTIVE_TENANT_HEADER, clearActiveTenantId, getActiveTenantId } from "./activeTenant";
import { resolveAnalyticsEnv } from "./analyticsEnv";

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://trusty-backend-678457575280.us-central1.run.app";

// Open the connection to the API before anything asks for it. The backend is a different origin from
// the site, so the first call otherwise pays DNS + TCP + TLS (~40ms measured) before its request even
// starts — and that first call is `subscription-status`, which gates the entire admin console.
//
// Done here rather than as a <link> in index.html because the origin is environment-dependent: alpha,
// dev and a local backend each use a different host, and a hardcoded one would warm a connection none
// of them use. This module is imported by the app entry, so it runs during bundle execution, well
// before React has mounted or Firebase has resolved a token.
//
// Best-effort by construction: an unused preconnect is discarded by the browser after a few seconds,
// and any failure here must never take the app down with it.
try {
  const link = document.createElement("link");
  link.rel = "preconnect";
  link.href = new URL(API_BASE_URL).origin;
  link.crossOrigin = "anonymous"; // matches the CORS mode of the fetches, or the socket can't be reused
  document.head.appendChild(link);
} catch {
  /* A malformed override or a non-DOM environment (jest) — the app works without the hint. */
}

/**
 * Every request to the backend is bounded (ENG-2810).
 *
 * Before this, nothing in this package set a timeout — `fetch()` with no `AbortSignal`, at 11 call
 * sites. A backend that accepts the connection and then never answers, which is what happened on
 * 2026-08-07, left the console hanging with no error, no retry and no way out but a reload. That is
 * the same failure ENG-2786 bounded on the server's own outbound calls; this is the other end of it.
 *
 * 15s matches `haven-account-client`, which is the same tier of caller. The extension uses 10s, but it
 * is a background worker retrying on an alarm — a person waiting on a page is a different trade.
 */
const HTTP_TIMEOUT_MS = 15_000;

/**
 * `fetch` that aborts after [timeoutMs].
 *
 * Honours a caller-supplied `signal` as well as the timeout, rather than replacing it: passing our own
 * signal straight through would silently break any caller that cancels on unmount, and React 18's
 * double-invoked effects make that a live pattern rather than a hypothetical one.
 */
export async function fetchWithTimeout(
    input: RequestInfo | URL,
    init: RequestInit = {},
    timeoutMs: number = HTTP_TIMEOUT_MS,
): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(new DOMException("Request timed out", "TimeoutError")), timeoutMs);

    const external = init.signal ?? undefined;
    const relayAbort = () => controller.abort(external?.reason);
    if (external) {
        if (external.aborted) controller.abort(external.reason);
        else external.addEventListener("abort", relayAbort, { once: true });
    }

    try {
        return await fetch(input, { ...init, signal: controller.signal });
    } finally {
        clearTimeout(timer);
        external?.removeEventListener("abort", relayAbort);
    }
}

/** Per-call knobs that aren't part of RequestInit. */
export interface AuthenticatedFetchConfig {
    /**
     * Send the console's selected active tenant (ENG-2387). Default true — every
     * console call carries it so the server re-resolves scope consistently.
     *
     * Pass false for the one thing that must see the caller's OWN tenant regardless
     * of what the console is looking at: the subscription state pushed to the
     * extension. See activeTenant.ts for why that distinction is load-bearing.
     *
     * ENG-3407: the caller's own calls (`isPersonalPath`) are never scoped, whatever this says.
     */
    scoped?: boolean;
}

/**
 * ENG-3407 — the caller's OWN calls, which never name the organization the console is viewing.
 *
 * The backend authorizes the active-tenant header on EVERY request, before any handler runs, and for a
 * provider's operator it applies their tier there too (`MspTierEnforcement`, ActiveTenant.kt): a
 * Read-only or Policy-only tier is refused any non-GET outside its policy paths. So while switched
 * into a client, their own calls were refused with "Your access to this organization does not allow
 * that." — deleting their account, their notification preferences, telemetry, a referral.
 *
 * None of these handlers reads the active tenant (checked 2026-09-27: each resolves the caller from the
 * token alone, or through `selectPolicyTenant` / `listDefaultTenantCandidates` for their own
 * governing organization), so sending the header could only ever cost them the request:
 *
 *  - `/account` — delete my account (SubscriptionRoutes); it touches only the caller's own memberships.
 *  - `/me/notification-preferences` — my notification choices (`resolveMemberPreferenceContext`,
 *    which picks the governing tenant with `selectPolicyTenant`, never the header).
 *  - `/me/home-metrics`, `/me/join-request` — my Home numbers, my request to join an organization.
 *  - `/me/flagged-emails`, `/me/flagged-links` — the emails and links behind two of those numbers (ENG-3610).
 *  - `/me/send-install-link` - mail my install link to my own address (ENG-3455).
 *  - `/me/family/visibility`, `/me/family/visibility/health` - what my family sees about me (ENG-3249).
 *  - `/events` — telemetry, attributed from the token.
 *  - `/my-referral`, `/my-custom-sites` — my referral, my personal custom sites.
 *  - `/surveys/submit` — the exit survey, sent as the account is deleted.
 *  - `/tenant` - creating an organization (ENG-3454): the caller administers nothing yet.
 *  - `/tenant/accept-invite` - accepting an invitation to join an organization: under `/tenant`,
 *    but it acts on the invitation's organization and the caller, not the one being viewed.
 *  - `/org-claim-links/preview`, `/org-claim-links/claim` - a demo org claim link (ENG-3832): it
 *    creates an organization for the caller, and has nothing to do with the one being viewed.
 *
 * Kept on purpose (the header is the point): `/subscription-status` (the console's own is the viewed
 * organization's), every other `/business/*` and `/tenant/*` call, and the billing portal (ENG-3421 names the
 * organization on purpose). Already unscoped at their call sites: `/me/tenants`, `/my-categories`,
 * `/my-sites`, and the extension's `/subscription-status`.
 */
const PERSONAL_PATHS: readonly string[] = [
    "/account",
    "/me/notification-preferences",
    "/me/home-metrics",
    "/me/flagged-emails",
    "/me/flagged-links",
    "/me/send-install-link",
    "/me/join-request",
    // ENG-3250: the caller's own family memberships; the leave names its family in the body.
    "/me/families",
    "/me/families/leave",
    // ENG-3249: what the caller's family can see about them. About the caller, so never the header.
    "/me/family/visibility",
    "/me/family/visibility/health",
    "/events",
    "/my-referral",
    "/my-custom-sites",
    "/surveys/submit",
    "/tenant/accept-invite",
    "/tenant",
    "/org-claim-links/preview",
    "/org-claim-links/claim",
];

/**
 * The only personal paths with ids under them (`/{category}`, `/{siteId}`). Everything else matches
 * exactly, so a route added later under a personal path — `/account/…`, `/events/…` — keeps the header
 * until someone checks its handler and lists it here.
 */
const PERSONAL_PREFIXES: readonly string[] = ["/me/notification-preferences/", "/my-custom-sites/"];

/** Whether [path] is one of the caller's own calls, which never carry the active-tenant header. */
export function isPersonalPath(path: string): boolean {
    const clean = path.split("?")[0];
    return PERSONAL_PATHS.includes(clean) || PERSONAL_PREFIXES.some((p) => clean.startsWith(p) && clean.length > p.length);
}

/**
 * The backend's active-tenant rejection (ActiveTenant.kt). Deliberately one opaque
 * message for every failure mode, so we match on it rather than on bare 403 — other
 * endpoints return 403 for reasons that have nothing to do with the tenant header.
 */
async function isActiveTenantRejection(response: Response): Promise<boolean> {
    try {
        const body = await response.clone().json();
        return typeof body?.error === "string" && body.error === "Not authorized for the requested tenant";
    } catch {
        return false;
    }
}

export async function authenticatedFetch(
    path: string,
    options: RequestInit = {},
    config: AuthenticatedFetchConfig = {},
): Promise<Response> {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const token = await currentUser.getIdToken();
    const headers = new Headers(options.headers);
    headers.set("Authorization", `Bearer ${token}`);
    if (options.body && typeof options.body === "string" && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
    }

    const activeTenantId = config.scoped === false || isPersonalPath(path) ? null : getActiveTenantId();
    if (activeTenantId) headers.set(ACTIVE_TENANT_HEADER, activeTenantId);

    const response = await fetchWithTimeout(`${API_BASE_URL}${path}`, { ...options, headers });

    // A stale selection — access revoked, or the tenant deleted since we stored it —
    // would otherwise 403 every console request forever, with no way out from the UI.
    // Drop the selection and reload into the caller's own tenant. Safe from looping:
    // once cleared, no header is sent, so this branch cannot be re-entered.
    if (activeTenantId && response.status === 403 && await isActiveTenantRejection(response)) {
        clearActiveTenantId();
        window.location.reload();
    }

    return response;
}


const ANALYTICS_ENV = resolveAnalyticsEnv(API_BASE_URL);

/**
 * Fire-and-forget analytics event to the backend Avro pipeline (POST /events). The backend attributes
 * the event to the signed-in user's email from the Firebase bearer token, so no email is sent in the
 * body. Best-effort: never throws and silently no-ops when signed out. `event` should be a name defined
 * in packages/events-schema; `properties` keys are snake_case matching that event's schema.
 */
export async function trackBackendEvent(
    event: string,
    properties: Record<string, unknown> = {},
): Promise<void> {
    try {
        await authenticatedFetch("/events", {
            method: "POST",
            body: JSON.stringify({
                id: crypto.randomUUID(),
                env: ANALYTICS_ENV,
                platform: "account",
                event,
                clientTime: new Date().toISOString(),
                schemaVersion: 1,
                properties: JSON.stringify(properties),
            }),
        });
    } catch {
        // Best-effort telemetry: signed-out or network failure must never surface to the user.
    }
}
